import { useMemo } from 'react';
import './Consignment.css';
import { ApiConsignment, ApiConsignmentTracking, ApiOrderItem, ApiConsignedArticle } from '../../types';
import { Link } from 'react-router-dom';
import { useFormatting } from '../../i18n/i18n';
import { useTranslation } from '../../i18n/i18n';
import { Img } from '../Img/Img';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Icon, delivery } from '../../components/Icon/Icon';
import { renderTemplate } from '../utils';
import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';

type ConsignmentProps = {
	consignment: ApiConsignment;
	vendorId: number;
	orderItems: ApiOrderItem[];
};

const ConsignmentTracking = ({ consignmentTracking }: { consignmentTracking: ApiConsignmentTracking }) => {
	const t = useTranslation().orderFulfillment;

	const trackingLinkByType: Record<string, string> = {
		'SE POST': 'https://tracking.postnord.com/se/?id={{trackingCode}}',
		'DK POST': 'https://www.postnord.dk/varktojer/track-trace/?shipmentId={{trackingCode}}',
		DHL: 'http://activetracing.dhl.com/DatPublic/datSelection.do?kr={{trackingCode}}',
		'DHL KOLLI':
			'https://activetracing.dhl.com/DatPublic/search.do?search=consignmentId&autoSearch=true&l=sv&a={{trackingCode}}',
		'Pickup post office': 'http://www.dhl.se/en/express/tracking.html?AWB={{trackingCode}}&brand=DHL',
		'Standard Cargo': 'http://www.dhl.se/en/express/tracking.html?AWB={{trackingCode}}&brand=DHL',
		Bring: 'https://tracking.bring.se/tracking/{{trackingCode}}',
		BringNo: 'https://sporing.bring.no/sporing/{{trackingCode}}',
		BringDk: 'https://tracking.bring.dk/tracking/{{trackingCode}}',
		UPS: 'https://wwwapps.ups.com/tracking/tracking.cgi?tracknum={{trackingCode}}&loc=sv_SE',
		'POST REF':
			'https://api2.postnord.com/widgets/ntt-widget/index-postnord-sv.html?searchCode=688b751c:34a0:0206:f044:26c1:7b16728e3c2a:720583&searchReference={{trackingCode}}',
		'POST GREE':
			'https://api2.postnord.com/widgets/ntt-widget/index-postnord-sv.html?searchCode=688b751c:34a0:6001:f044:26c1:7b16728e3c2a:949004&searchReference={{trackingCode}}',
		'POST EMO2':
			'https://api2.postnord.com/widgets/ntt-widget/index-SE-sv.html?searchCode=688b751c:34a0:0207:f044:26c1:7b16728e3c2a:648288&searchReference={{trackingCode}}',
		'POST EMO RET': '',
		'POST CREM':
			'https://api2.postnord.com/widgets/ntt-widget/index-SE-sv.html?searchCode=688b751c:34a0:6500:f044:26c1:7b16728e3c2a:694002&searchReference={{trackingCode}}',
		TNT: 'https://www.tnt.com/express/sv_se/site/shipping-tools/tracking.html?searchType=con&cons={{trackingCode}}',
		Schenker: 'https://www.dsv.com/mydsv/tracking-public/?refNumber={{trackingCode}}',
		SchenkerRef: 'https://www.dsv.com/mydsv/tracking-public/?refNumber={{trackingCode}}',
		'GLS NL':
			'https://www.gls-group.eu/276-I-PORTAL-WEB/dLink.jsp?un=0560004071&lc=EN&no=0560004071&key={{trackingCode}}',
		'GLS NL2':
			'https://services.gls-belgium.com/pgm/wwwdspenvc.pgm?REQUEST=24132619331702260133311730211602323013152301283516021617280512060404080115273326323037021416013126161527260209100{{trackingCode}}18',
		'GLS DK': 'https://www.nshiftportal.com/ui/sv/s?pp=a86a6ec7-d3c4-4174-8f21-aea6646981a0&q={{trackingCode}}',
		DPD: 'https://my.dpd.de/redirect.aspx?action=1&parcelno={{trackingCode}}',
		'DHL NL': 'https://my.dhlecommerce.nl/home/tracktrace/{{trackingCode}}',
		'POST DK': 'https://www.postnord.dk/varktojer/track-trace/?shipmentId={{trackingCode}}',
	};

	const url = renderTemplate(trackingLinkByType[consignmentTracking.type], {
		trackingCode: consignmentTracking.code,
	});

	return (
		<p>
			{t.trackingNumber}:{' '}
			<a href={url} target="_blank" className="tracking-link">
				{consignmentTracking.code}
			</a>
		</p>
	);
};

const ConsignedArticle = ({
	consignedArticle,
	orderItem,
}: {
	consignedArticle: ApiConsignedArticle;
	orderItem: ApiOrderItem;
}) => {
	return (
		<>
			{orderItem ? (
				<div className="row">
					<div className="product-image">
						<Img src={orderItem.image} width={50} height={50} alt={orderItem.product.name} />
					</div>
					<div className="product-details-wrapper">
						<div className="product-link">
							<Link to={'/-p-' + orderItem.product.id}>{orderItem.product.name}</Link>
						</div>
						<div className="article-number">{orderItem.product.displayId}</div>
						<div>{orderItem.quantity}</div>
						<div>{consignedArticle.quantity}</div>
					</div>
				</div>
			) : (
				<ErrorMessage message="Missing order item"></ErrorMessage>
			)}
		</>
	);
};

export const Consignment = ({ consignment, orderItems }: ConsignmentProps): JSX.Element => {
	const { dateTimeFormat } = useFormatting();

	const t = {
		...useTranslation().orderFulfillment,
		...useTranslation().product,
		...useTranslation().orderPages,
	};

	const orderItemsByTcId: Record<number, ApiOrderItem> = useMemo(() => {
		return Object.fromEntries(orderItems.map((e) => [e.product.tcId, e]));
	}, [orderItems]);

	return (
		<ContentCard>
			<p>
				{dateTimeFormat(new Date(consignment.dateCreated))} {t.shippedDelivery}{' '}
			</p>
			{consignment.tracking.map((tracking) => (
				<ConsignmentTracking key={tracking.code} consignmentTracking={tracking}></ConsignmentTracking>
			))}

			<p>
				<Icon shape={delivery}></Icon> {t.sentWithCarrier} {consignment.carrier}{' '}
			</p>

			<div className="consigned-article-wrapper">
				<div className="row">
					<div className="product-image">
						<span className="only-desktop">{t.product}:</span>
						<span className="only-mobile">{t.product}</span>
					</div>
					<div className="product-details-wrapper">
						<div className="product-link"></div>
						<div className="heading">
							<span className="only-desktop">{t.articleNumber}:</span>
							<span className="only-mobile">{t.articleNumber}</span>
						</div>
						<div className="heading">
							<span className="only-desktop">{t.amountOrdered}:</span>
							<span className="only-mobile">{t.amount}</span>
						</div>
						<div className="heading">
							<span className="only-desktop">{t.inThisPackage}:</span>
							<span className="only-mobile">{t.inThisPackage}</span>
						</div>
					</div>
				</div>
				{consignment.articles.map((consignedArticle) => (
					<ConsignedArticle
						key={consignedArticle.productId}
						consignedArticle={consignedArticle}
						orderItem={orderItemsByTcId[consignedArticle.productId]}
					></ConsignedArticle>
				))}
			</div>
		</ContentCard>
	);
};
